import { PanelBase } from "./PanelBase";
import type { GameContext } from "../../GameContext";

interface Skill {
  skillId: string;
  name: string;
  description: string;
  learned: boolean;
  requiredJobLevel: number;
}

/**
 * Per M04 (classjob-worker): GET /api/v1/characters/:id/skills, POST
 * /api/v1/characters/:id/skills/:skillId/learn. Growth-curve math itself
 * stays server-side (1.4) -- this panel lists what's learnable and sends
 * "learn" intents, it never decides eligibility client-side.
 */
export class SkillTree extends PanelBase {
  constructor(root: HTMLElement, ctx: GameContext, private readonly characterId: string) {
    super(root, ctx, "skill-tree");
  }

  protected async refresh(): Promise<void> {
    this.el.innerHTML = "<p>Loading skills…</p>";
    const result = await this.ctx.network.rest.get<Skill[]>(`/api/v1/characters/${this.characterId}/skills`);

    if (!result.ok) {
      this.el.innerHTML = `<p class="panel-error">${escapeHtml(result.message)}</p>`;
      return;
    }

    this.render(result.data);
  }

  private render(skills: Skill[]): void {
    this.el.innerHTML = "<h3>Skills</h3>";
    const list = document.createElement("div");
    list.className = "skill-list";

    for (const skill of skills) {
      const row = document.createElement("div");
      row.className = "skill-row";
      row.innerHTML = `
        <div class="skill-name">${escapeHtml(skill.name)} ${skill.learned ? "✓" : ""}</div>
        <div class="skill-desc">${escapeHtml(skill.description)}</div>
        <div class="skill-req">Requires job level ${skill.requiredJobLevel}</div>
      `;

      if (!skill.learned) {
        const learnBtn = document.createElement("button");
        learnBtn.textContent = "Learn";
        learnBtn.addEventListener("click", () => void this.learnSkill(skill.skillId));
        row.appendChild(learnBtn);
      }

      list.appendChild(row);
    }
    this.el.appendChild(list);
  }

  private async learnSkill(skillId: string): Promise<void> {
    const result = await this.ctx.network.rest.post(
      `/api/v1/characters/${this.characterId}/skills/${skillId}/learn`,
      {}
    );
    if (result.ok) await this.refresh();
  }
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}
